import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { StyleLike } from 'ol/style/Style';
import { Feature } from 'ol';
import { connect } from 'react-redux';
import { Vegnettlenke } from '@/domain/vegnett/Vegnettlenke';
import * as selectors from '../../../selectors/selectors';
import { RootState } from '@/state';
import { roadNetLinkToFeature, roadNetNodeToFeature } from '@/utils/SpatialHelper';
import { lineStyle } from '../style/LineStyle';
import {
    arrowedLineStyle,
    directionalArrowedLineStyle,
    directionalLineStyle,
    featureStyle,
} from '../style/Style';
import React from 'react';
import { MapContext } from '@/state/context/MapContext';
import { Geometry, Point } from 'ol/geom';
import { ColorIndex, resolveColor } from '@/utils/ColorHelper';
import { Node } from '@/domain/vegnett/Node';
import { FeatureColorKey } from './FeatureLayerType';

interface StateProps {
    links: Record<string, Vegnettlenke[]>;
    nodes: Node[];
    roadNetDirection: string;
    zoom: number;
}

type Props = StateProps;

export class LinkQueryFeatureLayer extends React.Component<Props, Record<string, never>> {
    static contextType = MapContext;
    declare context: React.ContextType<typeof MapContext>;

    private readonly lineSource = new VectorSource<Feature<Geometry>>();
    private readonly nodeSource = new VectorSource<Feature<Point>>();
    private readonly layer = new VectorLayer<VectorSource<Feature<Geometry>>>({
        source: this.lineSource,
        style: lineStyle as StyleLike,
        zIndex: 5,
    });
    private readonly nodeLayer = new VectorLayer<VectorSource<Feature<Point>>>({
        source: this.nodeSource,
        style: featureStyle as StyleLike,
        zIndex: 6,
    });
    private lineLayerAttached = false;
    private nodeLayerAttached = false;
    private currentLineStyle: StyleLike = lineStyle as StyleLike;

    componentDidMount() {
        this.layer.set('name', 'linkQueryLayer');
        this.nodeLayer.set('name', 'nodeLayer');
        this.updateLinks();
        this.updateNodes();
        this.updateStyle();
        this.syncLayerAttachments();
    }

    componentDidUpdate(prevProps: Props) {
        const linksChanged =
            prevProps.links !== this.props.links ||
            prevProps.roadNetDirection !== this.props.roadNetDirection;

        if (linksChanged) {
            this.updateLinks();
        }
        if (prevProps.nodes !== this.props.nodes) {
            this.updateNodes();
        }
        if (linksChanged || prevProps.zoom !== this.props.zoom) {
            this.updateStyle();
        }
        this.syncLayerAttachments();
    }

    componentWillUnmount() {
        if (this.lineLayerAttached) {
            this.context.map.removeLayer(this.layer);
        }
        if (this.nodeLayerAttached) {
            this.context.map.removeLayer(this.nodeLayer);
        }
    }

    private updateLinks() {
        const features = Object.values(this.props.links)
            .reduce(
                (all, links, index) => [
                    ...all,
                    ...links.map(link => ({ link, color: new ColorIndex(index, index) })),
                ],
                [] as { link: Vegnettlenke; color: ColorIndex }[]
            )
            .filter(({ link }) => link.geometri && link.geometri.wkt)
            .map(({ link, color }) => {
                const feature = roadNetLinkToFeature(link, this.props.roadNetDirection);
                feature.set(FeatureColorKey, resolveColor(color));
                return feature;
            });

        replaceSourceFeatures(this.lineSource, features);
    }

    private updateNodes() {
        replaceSourceFeatures(
            this.nodeSource,
            this.props.nodes.map(node => roadNetNodeToFeature(node))
        );
    }

    private updateStyle() {
        const style = (
            this.props.roadNetDirection === 'geometri'
                ? this.props.zoom >= 14
                    ? arrowedLineStyle
                    : lineStyle
                : this.props.zoom >= 14
                  ? directionalArrowedLineStyle
                  : directionalLineStyle
        ) as StyleLike;

        if (style !== this.currentLineStyle) {
            this.layer.setStyle(style);
            this.currentLineStyle = style;
        }
    }

    private syncLayerAttachments() {
        const hasLines = !this.lineSource.isEmpty();
        const hasNodes = !this.nodeSource.isEmpty();

        if (hasLines && !this.lineLayerAttached) {
            this.context.map.addLayer(this.layer);
            this.lineLayerAttached = true;
        } else if (!hasLines && this.lineLayerAttached) {
            this.context.map.removeLayer(this.layer);
            this.lineLayerAttached = false;
        }

        if (hasNodes && !this.nodeLayerAttached) {
            this.context.map.addLayer(this.nodeLayer);
            this.nodeLayerAttached = true;
        } else if (!hasNodes && this.nodeLayerAttached) {
            this.context.map.removeLayer(this.nodeLayer);
            this.nodeLayerAttached = false;
        }
    }

    render() {
        return null;
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    return {
        nodes: selectors.stateValgt(state).nodes,
        links: selectors.stateValgt(state).linkQueries,
        roadNetDirection: selectors.stateVegnettDirection(state),
        zoom: selectors.stateZoom(state),
    };
};

export const LinkQueryLayer = connect(mapStateToProps)(LinkQueryFeatureLayer);

function replaceSourceFeatures<T extends Feature>(
    source: VectorSource<T>,
    features: T[]
) {
    if (source.isEmpty() && features.length === 0) {
        return;
    }

    source.clear();
    if (features.length > 0) {
        source.addFeatures(features);
    }
}
